// Auto-rotating promo banner carousel — fetched from /api/banners.
// Renders nothing when no banners are active. Clicking a banner navigates
// to its `link` if set (internal path → react-router; absolute → new tab).
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const ROTATE_MS = 5000;

export default function BannerCarousel() {
  const [banners, setBanners] = useState([]);
  const [index, setIndex] = useState(0);
  const nav = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    if (!API) return;
    axios.get(`${API}/api/banners`).then(({ data }) => {
      setBanners(data.banners || []);
    }).catch(() => { /* silent */ });
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    timer.current = setInterval(() => {
      setIndex((i) => (i + 1) % banners.length);
    }, ROTATE_MS);
    return () => clearInterval(timer.current);
  }, [banners.length]);

  if (!banners.length) return null;

  const handleClick = (b) => {
    if (!b.link) return;
    if (/^https?:\/\//.test(b.link)) {
      window.open(b.link, "_blank", "noopener,noreferrer");
    } else {
      nav(b.link);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4" data-testid="banner-carousel">
      <div className="relative rounded-3xl overflow-hidden talabya-shadow-soft h-40 sm:h-52 md:h-60 bg-[var(--surface-alt)]">
        {banners.map((b, i) => (
          <button
            key={b.id}
            type="button"
            onClick={() => handleClick(b)}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`}
            data-testid={`banner-slide-${i}`}
            aria-hidden={i !== index}
            tabIndex={i === index ? 0 : -1}
          >
            <img src={`${API}${b.image_url}`} alt={b.title || "إعلان"}
              className="w-full h-full object-cover"
              loading={i === 0 ? "eager" : "lazy"}/>
            {b.title && (
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/60 to-transparent text-white text-right">
                <div className="font-black text-sm sm:text-base" style={{fontFamily:"Cairo"}}>{b.title}</div>
              </div>
            )}
          </button>
        ))}
      </div>
      {banners.length > 1 && (
        <div className="mt-2 flex justify-center gap-1.5" data-testid="banner-dots">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${i === index ? "w-6 bg-primary-brand" : "w-1.5 bg-[var(--border)]"}`}
              aria-label={`الانتقال للبانر ${i + 1}`}
              data-testid={`banner-dot-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
